import {
  DownloadCloud,
  FileText,
  Plus,
} from 'lucide-react';
import { Fab } from '@/components/ui/Fab';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createProcesso,
  eDeColega,
  fetchProcessos,
  podePreparar,
  updateProcesso,
  type OrigemDaVisao,
  type Processo,
} from '@/api/client';
import { useSouGestor, useVisaoDoEscritorio } from '@/hooks/useVisaoDoEscritorio';
import { useAuthOpcional } from '@/context/AuthProvider';
import type { Responsavel } from '@/api/distribuicao';
import { ResponsaveisDialog } from '@/components/escritorio/ResponsaveisDialog';
import { useDistribuicao } from '@/hooks/useDistribuicao';
import { fetchProcessosPorVencimento } from '@/api/regua';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/PageHeader';
import { useNotification } from '@/context/NotificationProvider';
import { useAcervoImport } from '@/hooks/useAcervoImport';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { useProcessoJudicial } from '@/hooks/useProcessoJudicial';
import { useOrdemDoAcervo } from '@/hooks/useOrdemDoAcervo';
import { useSmartSearch } from '@/hooks/useSmartSearch';
import { Spinner } from '@/components/ui/spinner';
import { classifyError, getUserVisibleErrorMessage, notifyMutationError } from '@/lib/errors';
import { CODIGO_CERT_EXPIRADO } from '@/lib/errors/certificado';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';

import { ACERVO_SEARCH_FIELDS } from './acervoSearch';
import { AcervoImportCard } from './AcervoImportCard';
import { ConsultaProcessualCard } from './ConsultaProcessualCard';
import { ProcessoCreateDialog } from './ProcessoCreateDialog';
import { ProcessoEditDialog } from './ProcessoEditDialog';
import { ProcessoFilterBar, type AbaDoAcervo, type Vencimento } from './ProcessoFilterBar';
import { SelecaoEmLote } from './SelecaoEmLote';
import { ProcessoTable } from './ProcessoTable';
import { useAtuacao, usePodeColaborar } from '@/lib/delegacao/atuacao';
import { useAplicarTagsEmLote, useTags } from '@/hooks/useTags';
import { casaFacetas, facetasDoFiltro } from '@/lib/tags/agrupar';
import { TagsLoteDialog } from '@/components/tags/TagsLoteDialog';
import { CACHES_DE_PROCESSO, useTagsNoCache } from '@/hooks/useTagsNoCache';
import type { TagResumo } from '@/api/tags';
import { hojeLocal } from '@/pages/dashboard/reguaDias';

import { dataDoCompromisso } from './ColunasDoAcervo';

const DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;

/** Quem cuida do processo (§19): os responsáveis gravados ou, sem distribuição, o dono. */
function quemCuidaDe(processo: Processo, meuId: string | undefined): string[] {
  const rs = processo.responsaveis ?? [];
  if (rs.length > 0) return rs.map((r) => `${r.tipo}:${r.id}`);
  return [`USUARIO:${processo.dono?.id ?? meuId}`];
}

/**
 * O filtro "Quem cuida" do gestor: "Sem distribuir", um advogado ou uma equipe. O advogado casa
 * também com os processos das equipes em que ele está; "Você" é o "sob meus cuidados" que o
 * servidor calcula (o meu que o Gestor não passou a outros e o do colega de que eu cuido).
 */
function casaQuemCuida(
  processo: Processo,
  valor: string,
  meuId: string | undefined,
  membrosDaEquipe: Map<string, Set<string>>,
): boolean {
  if (valor === 'todos') return true;
  if (valor === 'sem') return (processo.responsaveis ?? []).length === 0;
  if (meuId && valor === `USUARIO:${meuId}`) {
    return eDeColega(processo) ? processo.sob_meus_cuidados === true : processo.sob_meus_cuidados !== false;
  }
  const cuidam = quemCuidaDe(processo, meuId);
  if (cuidam.includes(valor)) return true;
  const pessoa = valor.startsWith('USUARIO:') ? valor.slice('USUARIO:'.length) : null;
  return !!pessoa && cuidam.some((c) => c.startsWith('EQUIPE:') && membrosDaEquipe.get(c)?.has(pessoa));
}

function ProcessesPage() {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('q') ?? '';
  // O painel abre o acervo pelos prazos de um dia (Bloco F de docs/PLANO_CONTAGEM_PRAZOS.md):
  // o filtro é do servidor, que conhece os vencimentos.
  const venceDe = searchParams.get('vence_de') ?? '';
  const venceAte = searchParams.get('vence_ate') || venceDe;
  const porVencimento = DATA_ISO.test(venceDe) && DATA_ISO.test(venceAte) && venceDe <= venceAte;
  // Main query. Quem é de um escritório lê a visão (o próprio acervo, o que cuida e o que
  // colegas compartilharam, o mesmo CNJ uma vez só) num cache próprio; o gestor escolhe
  // "Todo o escritório", que o servidor traz com o acervo de todos. Sem escritório, o de sempre.
  const visao = useVisaoDoEscritorio({ comEstado: true });
  const visaoAtiva = visao.ativa;
  const souGestor = useSouGestor();
  const user = useAuthOpcional()?.user;
  // O gestor carrega o acervo de todo o escritório e filtra pelo "Quem cuida" (que abre em
  // "Você"); quem não é gestor carrega o seu.
  const origemDoServidor: OrigemDaVisao = souGestor ? 'escritorio' : 'todos';
  const todos = useQuery({
    queryKey: visaoAtiva ? [...queryKeys.processos.all, 'visao', origemDoServidor] : [...queryKeys.processos.all],
    queryFn: () => fetchProcessos(visaoAtiva ? { origem: origemDoServidor } : undefined),
    meta: queryPersistMeta.nonEmpty,
    // Com escritório, o que os colegas mudam (tags, quem cuida, intimações) não passa por esta
    // tela: mostra o guardado na hora e sempre confere com o servidor ao abrir.
    refetchOnMount: visaoAtiva ? 'always' : undefined,
    // Só depois de saber se há escritório: sem isso a tela pedia o acervo próprio, e
    // então a visão, trocando a lista na frente do usuário (offline, ficava na errada).
    enabled: visao.conhecida,
  });
  const filtrados = useQuery({
    queryKey: [...queryKeys.processos.porVencimento(venceDe, venceAte)],
    queryFn: () => fetchProcessosPorVencimento(venceDe, venceAte),
    enabled: porVencimento,
  });
  const { data, isLoading, dataUpdatedAt, error } = porVencimento ? filtrados : todos;

  // O "Vence em…" mora na URL (o painel abre o acervo por ela).
  const mudarVencimento = (v: Vencimento | null) =>
    setSearchParams(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        if (v) {
          proximo.set('vence_de', v.de);
          proximo.set('vence_ate', v.ate);
        } else {
          proximo.delete('vence_de');
          proximo.delete('vence_ate');
        }
        return proximo;
      },
      { replace: true },
    );

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<Processo | null>(null);

  // Filter state
  const [aba, setAba] = useState<AbaDoAcervo>('todos');
  const [vinculo, setVinculo] = useState('todos');
  // ``null``: ainda não mexeu — vale "Você".
  const [quemCuidaEscolhido, setQuemCuida] = useState<string | null>(null);
  // O Gestor distribui pelo acervo também (§19): "Quem cuida…" e a seleção em lote.
  const distribuicao = useDistribuicao();
  const [distribuindo, setDistribuindo] = useState<{
    ids: string[];
    rotulo: string;
    iniciais: Responsavel[] | undefined;
  } | null>(null);
  const [tribunal, setTribunal] = useState('todos');
  const [instancia, setInstancia] = useState('todas');
  const [searchQuery, setSearchQuery] = useState(queryParam);
  // Filtro por tags (PLANO_TAGS D7) no cliente, como os demais: a lista é o acervo
  // inteiro e a tela funciona offline.
  const [filtroTags, setFiltroTags] = useState<string[]>([]);
  const tagsQuery = useTags();
  // Seleção para tags em lote (D13).
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [loteAberto, setLoteAberto] = useState(false);
  const lote = useAplicarTagsEmLote('processos', [queryKeys.processos.all]);
  const tagsNoCache = useTagsNoCache(CACHES_DE_PROCESSO);
  // Processo com o diálogo de tags aberto: fica na lista mesmo que a edição o tire
  // do filtro de tags — senão a linha (e o diálogo) desmontaria no meio do toque.
  const [emEdicao, setEmEdicao] = useState<string | null>(null);

  // Import do acervo (Data Lake): só cadastra processos e clientes, sem baixar documentos.
  const acervo = useAcervoImport();
  const [importando, setImportando] = useState(false);
  const [precondicao, setPrecondicao] = useState<'pdpj' | 'certificado' | 'vencido' | null>(null);

  // Em tela larga o aviso do import fica ao lado do botão; quando o texto precisa de espaço
  // (falha, ou o limite do plano com o link de planos) e no celular, ele vira faixa abaixo.
  const telaLarga = useMediaQuery('(min-width: 768px)');
  const noCelular = !useMediaQuery('(min-width: 640px)');
  const avisoDetalhado =
    acervo.progresso?.status === 'FAILED' ||
    (acervo.progresso?.status === 'COMPLETED' && (acervo.progresso.cortados_pelo_plano ?? 0) > 0);
  const avisoNoCabecalho = telaLarga && !avisoDetalhado;
  // Importar o acervo e cadastrar processo são do titular (rotas não delegáveis).
  const atuacao = useAtuacao();
  const podeCadastrar = !atuacao;
  // Tags em lote (aplicar ou remover): só quem pode colaborar (na delegação de Leitura, não).
  const podeColaborar = usePodeColaborar();

  const cancelarImport = () => {
    acervo.cancelar().catch((err: unknown) => notifyMutationError(err, showError));
  };

  const handleImportarAcervo = async () => {
    setImportando(true);
    setPrecondicao(null);
    try {
      await acervo.iniciar();
    } catch (err) {
      const { kind, codigo } = classifyError(err);
      if (kind === 'pdpj_required') {
        setPrecondicao('pdpj');
      } else if (kind === 'certificate_required') {
        setPrecondicao(codigo === CODIGO_CERT_EXPIRADO ? 'vencido' : 'certificado');
      } else {
        notifyMutationError(err, showError);
      }
    } finally {
      setImportando(false);
    }
  };

  // Sync searchQuery ↔ URL ?q= bidirecionalmente
  useEffect(() => {
    setSearchQuery(queryParam);
  }, [queryParam]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    // Atualizar URL com debounce implícito via onSearchChange; o filtro de
    // vencimento (vindo do painel) continua.
    setSearchParams(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        if (value) proximo.set('q', value);
        else proximo.delete('q');
        return proximo;
      },
      { replace: true },
    );
  };

  // External process search — usa searchQuery (synced com URL)
  const digitsOnly = searchQuery.replace(/\D/g, '');
  const isProcessNumber = digitsOnly.length === 20;

  // O número que já está na lista (o próprio ou o de um colega que se vê) não vai aos
  // tribunais: a tabela já o mostra.
  const naLista =
    isProcessNumber && !!todos.data?.some((p) => p.numero.replace(/\D/g, '') === digitsOnly);
  const externalQuery = useProcessoJudicial(searchQuery, {
    enabled: !!searchQuery && isProcessNumber && !naLista,
    pollOn202: true,
  });
  const processoExterno = externalQuery.data;
  const loadingExterno = externalQuery.isLoading;
  const erroExterno = externalQuery.error;
  // 202: o backend disparou a carga inicial e ainda não tem dados. Sem esses
  // dois estados o card sumia e a tabela vazia lia como "nada encontrado"
  // enquanto a consulta externa ainda rodava.
  const preparandoExterno =
    externalQuery.accepted && !processoExterno && !externalQuery.pollExpired;
  const consultaExpirada = externalQuery.pollExpired && !processoExterno;

  // Por DÍGITOS, não pelo texto: "0802546.26.2025.8.20.5162" (pontos no lugar
  // do hífen, como vem de muito copiar-e-colar) é o mesmo processo que o
  // "0802546-26.2025.8.20.5162" gravado.
  // Sobre o acervo inteiro: no filtrado pelo painel, quem não vence no dia continua cadastrado.
  // Só o próprio acervo. O CNJ que só um colega tem já está na tabela (``naLista``), sem o card.
  const isTracked = !!todos.data?.some(
    (p) =>
      !eDeColega(p) &&
      (p.numero === processoExterno?.numero_processo ||
        (isProcessNumber && p.numero.replace(/\D/g, '') === digitsOnly)),
  );

  const [acompanharLoading, setAcompanharLoading] = useState(false);

  const handleAcompanhar = async () => {
    if (!processoExterno) return;
    setAcompanharLoading(true);
    try {
      const tram = processoExterno.tramitacao_atual;
      await createProcesso({
        numero: processoExterno.numero_processo,
        tribunal: processoExterno.sigla_tribunal,
        classe: tram.classe[0]?.descricao ?? 'Não informada',
        assunto: tram.assunto[0]?.descricao ?? null,
        status: 'ATIVO',
        tipo: 'ACOMPANHAMENTO',
        possui_permissao_peticionar: tram.permite_peticionar,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.processos.all });
      showSuccess('Processo adicionado para acompanhamento');
    } catch (err) {
      const msg = getUserVisibleErrorMessage(err);
      if (msg) showError(msg);
    } finally {
      setAcompanharLoading(false);
    }
  };

  // Archive mutation (used by table)
  const archiveMutation = useOfflineMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Processo> }) => updateProcesso(id, payload),
    outbox: {
      type: 'processo_editar',
      getPayload: ({ id, payload }) => ({
        method: 'PUT',
        url: `/processos/${id}`,
        body: payload,
      }),
      getDescription: ({ id }) => `Editar processo ${id}`,
    },
    invalidateKeys: [[...queryKeys.processos.all]],
    onSuccess: () => {
      showSuccess('Processo arquivado com sucesso');
    },
    onQueued: () => {
      showSuccess('Processo arquivado com sucesso (será sincronizado quando houver conexão)');
    },
  });

  // Computed: aba, vínculo, tribunal e instância primeiro; a busca inteligente refina
  // o que sobrou (o "N de M" conta sobre a lista que os filtros mostram).
  const facetas = useMemo(
    () => facetasDoFiltro(filtroTags, tagsQuery.tagsPorId),
    [filtroTags, tagsQuery.tagsPorId],
  );
  // O "Quem cuida" some no recorte de vencimento (a lista do servidor não o traz).
  const quemCuidaVale = visaoAtiva && !porVencimento;
  const daOrigem = data;
  // As opções do "Quem cuida": do cadastro do escritório (todos os advogados e equipes, mesmo
  // os que ainda não cuidam de nada) — você no topo, depois os colegas e as equipes.
  const opcoesQuemCuida = useMemo(() => {
    const pessoas = (distribuicao?.membros ?? [])
      .filter((m) => m.usuario_id !== user?.id)
      .map((m) => ({ value: `USUARIO:${m.usuario_id}`, label: m.usuario_nome }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    const equipes = (distribuicao?.equipes ?? [])
      .map((e) => ({ value: `EQUIPE:${e.id}`, label: `Equipe ${e.nome}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    const voce = user?.id ? [{ value: `USUARIO:${user.id}`, label: 'Você' }] : [];
    return [{ value: 'sem', label: 'Sem distribuir' }, ...voce, ...pessoas, ...equipes];
  }, [distribuicao?.membros, distribuicao?.equipes, user?.id]);
  const temColegas = (distribuicao?.membros.length ?? 0) > 1;
  const membrosDaEquipe = useMemo(
    () =>
      new Map(
        (distribuicao?.equipes ?? []).map((e) => [`EQUIPE:${e.id}`, new Set(e.membros.map((m) => m.usuario_id))]),
      ),
    [distribuicao?.equipes],
  );
  // A opção escolhida que sumiu (a equipe excluída, quem saiu) volta a "Você".
  const quemCuidaPadrao = user?.id ? `USUARIO:${user.id}` : 'todos';
  const quemCuida =
    quemCuidaEscolhido !== null &&
    (quemCuidaEscolhido === 'todos' || opcoesQuemCuida.some((o) => o.value === quemCuidaEscolhido))
      ? quemCuidaEscolhido
      : quemCuidaPadrao;
  const filteredByControls = useMemo(
    () =>
      (daOrigem ?? []).filter((processo) => {
        const matchesAba =
          aba === 'todos' || (aba === 'favoritos' ? !!processo.favorito : processo.status === aba);
        const matchesTribunal = tribunal === 'todos' || processo.tribunal === tribunal;
        const matchesInstancia =
          instancia === 'todas' || (processo.instancia ?? 'nenhuma') === instancia;
        const matchesVinculo =
          vinculo === 'todos' ||
          (vinculo === 'patrocinio' && processo.tipo === 'PATROCINIO') ||
          (vinculo === 'acompanhamento' && processo.tipo === 'ACOMPANHAMENTO');
        const matchesTags = processo.id === emEdicao || casaFacetas(processo.tags, facetas);
        // O número buscado respeita o "Quem cuida" (decisão do Adriano, 27/10): fora dele, a
        // tabela vazia diz onde o processo está (``buscadoForaDeQuemCuida``).
        const matchesQuemCuida =
          !quemCuidaVale || !souGestor || !temColegas || casaQuemCuida(processo, quemCuida, user?.id, membrosDaEquipe);
        return matchesAba && matchesTribunal && matchesInstancia && matchesVinculo && matchesTags && matchesQuemCuida;
      }),
    [daOrigem, aba, tribunal, instancia, vinculo, facetas, emEdicao, quemCuidaVale, souGestor, temColegas, quemCuida, user?.id, membrosDaEquipe],
  );
  // O número buscado está no acervo, mas o "Quem cuida" o tira: como ele não vai aos tribunais
  // (``naLista``), sem isto a tabela vazia leria como "não existe".
  const filtrandoQuemCuida = quemCuidaVale && souGestor && temColegas && quemCuida !== 'todos';
  const buscadoForaDeQuemCuida =
    naLista &&
    filtrandoQuemCuida &&
    !filteredByControls.some((p) => p.numero.replace(/\D/g, '') === digitsOnly);
  const search = useSmartSearch(filteredByControls, searchQuery, ACERVO_SEARCH_FIELDS);
  // Ordem pelo compromisso mais próximo (o padrão do filtro "Ordem" da barra): o prazo ou a
  // audiência que vier primeiro — o vencido da semana antes de tudo — e os sem nenhum dos dois
  // no fim, na ordem do acervo (o sort é estável).
  const [ordenarPorVencimento, setOrdenarPorVencimento] = useOrdemDoAcervo(user?.id);
  // A página do acervo vai na URL (o "Voltar" dos autos a devolve) e volta à 1 quando um
  // filtro muda — pela chave dos filtros, sem efeito.
  const chaveDosFiltros = JSON.stringify([
    aba,
    quemCuida,
    vinculo,
    tribunal,
    instancia,
    searchQuery,
    filtroTags,
    ordenarPorVencimento,
    venceDe,
    venceAte,
  ]);
  const [paginaDosFiltros, setPaginaDosFiltros] = useState(() => ({
    chave: chaveDosFiltros,
    numero: Number(searchParams.get('pagina')) || 1,
  }));
  const pagina = paginaDosFiltros.chave === chaveDosFiltros ? paginaDosFiltros.numero : 1;
  const mudarPagina = (numero: number) => {
    setPaginaDosFiltros({ chave: chaveDosFiltros, numero });
    setSearchParams(
      (atual) => {
        const proximos = new URLSearchParams(atual);
        if (numero > 1) proximos.set('pagina', String(numero));
        else proximos.delete('pagina');
        return proximos;
      },
      { replace: true },
    );
  };
  const filteredData = useMemo(() => {
    if (!data) return undefined;
    if (!ordenarPorVencimento) return search.results;
    // Primeiro os que têm compromisso pela frente, do mais cedo; depois os de prazo vencido
    // na semana, do mais recente; por fim os sem nenhum, na ordem do acervo.
    const hoje = hojeLocal();
    const chave = (p: Processo): [number, string] => {
      const proximo = dataDoCompromisso(p, hoje);
      if (proximo) return [0, proximo];
      return p.vencimento ? [1, p.vencimento.data] : [2, ''];
    };
    const chaves = new Map(search.results.map((p) => [p.id, chave(p)]));
    return [...search.results].sort((a, b) => {
      const [ga, va] = chaves.get(a.id)!;
      const [gb, vb] = chaves.get(b.id)!;
      if (ga !== gb) return ga - gb;
      if (va === vb) return 0;
      return (ga === 1 ? va > vb : va < vb) ? -1 : 1;
    });
  }, [data, search.results, ordenarPorVencimento]);

  const stats = {
    total: daOrigem?.length ?? 0,
    patrocinio: daOrigem?.filter((p) => p.tipo === 'PATROCINIO').length ?? 0,
    acompanhamento: daOrigem?.filter((p) => p.tipo === 'ACOMPANHAMENTO').length ?? 0,
    ativos: daOrigem?.filter((p) => p.status === 'ATIVO').length ?? 0,
  };
  // Do acervo inteiro: o filtro de vencimento não pode sumir com tribunais da lista.
  const tribunais = useMemo(
    () => [...new Set((todos.data ?? []).map((p) => p.tribunal).filter(Boolean))].sort(),
    [todos.data],
  );

  // A seleção do lote vale para o que está na tela: mudar filtro, aba ou busca a
  // limpa — senão "Tags em lote" gravaria em processos que a pessoa não vê mais.
  useEffect(() => {
    setSelecionados(new Set());
  }, [filtroTags, aba, quemCuida, vinculo, tribunal, instancia, venceDe, venceAte, searchQuery]);

  // O que o Gestor distribui: o próprio e o de quem é membro do escritório em que ele é
  // gestor (o de um colega de OUTRO escritório não é dele distribuir).
  const membrosDaDistribuicao = useMemo(
    () => new Set((distribuicao?.membros ?? []).map((m) => m.usuario_id)),
    [distribuicao?.membros],
  );
  const distribuivel = useCallback(
    (p: Processo) => !p.dono || membrosDaDistribuicao.has(p.dono.id),
    [membrosDaDistribuicao],
  );
  const distribuirSelecionados = () => {
    const visiveis = new Map((filteredData ?? []).map((p) => [p.id, p]));
    const escolhidos = [...selecionados]
      .map((id) => visiveis.get(id))
      .filter((p): p is Processo => !!p && distribuivel(p));
    if (escolhidos.length === 0) return;
    setDistribuindo({
      ids: escolhidos.map((p) => p.id),
      rotulo: escolhidos.length === 1 ? `Processo ${escolhidos[0].numero}` : `${escolhidos.length} processos`,
      // Um só: quem cuida hoje vem do servidor, no escritório em que se distribui.
      iniciais: escolhidos.length === 1 ? undefined : [],
    });
  };

  const aplicarLote = async (tagIds: string[], acao: 'adicionar' | 'remover') => {
    // Tags no próprio e no do colega em que colabora (Bloco 3); o de Leitura selecionado é
    // para distribuir (§19).
    const visiveis = new Set((filteredData ?? []).filter((p) => podePreparar(p)).map((p) => p.id));
    const ids = [...selecionados].filter((id) => visiveis.has(id));
    // Otimista: o lote entra no cache antes da resposta (e antes da fila, offline).
    // Sem isso, um toque individual feito depois — que manda o conjunto que a tela
    // mostra — apagaria a tag do lote ao ser sincronizado.
    await tagsNoCache.cancelar();
    const escolhidas = tagIds
      .map((id) => tagsQuery.tagsPorId.get(id))
      .filter((t): t is NonNullable<typeof t> => !!t)
      .map((t): TagResumo => ({
        id: t.id,
        nome: t.nome,
        cor: t.cor,
        categoria_id: t.categoria_id,
      }));
    for (const processo of data ?? []) {
      if (!ids.includes(processo.id)) continue;
      const atuais = processo.tags ?? [];
      const novas =
        acao === 'adicionar'
          ? [...atuais, ...escolhidas.filter((t) => !atuais.some((a) => a.id === t.id))]
          : atuais.filter((a) => !tagIds.includes(a.id));
      tagsNoCache.escrever(processo.id, novas);
    }
    try {
      await lote.mutateAsync({ ids, [acao]: tagIds });
    } catch (err) {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.processos.all] });
      throw err;
    }
    setSelecionados(new Set());
  };

  const quantidadeDeTags = (filteredData ?? []).filter((p) => selecionados.has(p.id) && podePreparar(p)).length;

  // Handlers
  const handleEdit = (processo: Processo) => {
    setSelectedProcess(processo);
    setEditOpen(true);
  };

  const handleArchiveProcess = async (processo: Processo) => {
    const confirmed = window.confirm(`Arquivar o processo ${processo.numero}?`);
    if (!confirmed) return;
    try {
      await archiveMutation.mutateAsync({
        id: processo.id,
        payload: { status: 'ENCERRADO' },
      });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.processos.all] });
    } catch (error: unknown) {
      const msg = getUserVisibleErrorMessage(error);
      if (msg) showError(msg);
    }
  };

  const selecaoEmLote = (
    <SelecaoEmLote
      quantidade={selecionados.size}
      quantidadeDeTags={quantidadeDeTags}
      onAplicarTags={() => setLoteAberto(true)}
      onDistribuir={distribuicao ? distribuirSelecionados : undefined}
      onLimpar={() => setSelecionados(new Set())}
    />
  );

  return (
    // Com a barra da seleção no rodapé (celular), o último cartão não pode ficar por baixo dela.
    <div className={noCelular && selecionados.size > 0 ? 'space-y-6 pb-20' : 'space-y-6'}>
      <PageHeader
        icon={<FileText />}
        title="Acervo"
        subtitle="Gerencie todos os seus processos judiciais"
        dataUpdatedAt={dataUpdatedAt}
        actions={
          <div className="flex items-center gap-2">
            {podeCadastrar && avisoNoCabecalho && (
              <AcervoImportCard
                compacto
                progresso={acervo.progresso}
                onCancelar={cancelarImport}
                onDispensar={acervo.dispensar}
              />
            )}
            {podeCadastrar && (
              <>
                {/* No celular, só o ícone na linha do título (o "+" é o FAB). */}
                <Button
                  variant="outline"
                  onClick={handleImportarAcervo}
                  disabled={importando || acervo.emAndamento}
                  aria-label="Importar meus processos"
                  className="h-11 w-11 p-0 sm:h-9 sm:w-auto sm:px-4"
                >
                  {importando ? <Spinner size={16} className="text-inherit sm:mr-2" /> : <DownloadCloud className="h-4 w-4 sm:mr-2" />}
                  <span className="hidden sm:inline">Importar meus processos</span>
                </Button>
                <Button
                  onClick={() => setCreateOpen(true)}
                  className="hidden sm:inline-flex"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Processo
                </Button>
              </>
            )}
          </div>
        }
      />

      {precondicao && (
        <Alert variant="warning">
          <AlertDescription>
            {precondicao === 'pdpj'
              ? 'Conecte-se à PDPJ para importar seus processos.'
              : precondicao === 'vencido'
                ? 'Seu certificado digital venceu. Envie o renovado para importar seus processos.'
                : 'Cadastre seu certificado digital para importar seus processos.'}{' '}
            <Link to="/app/certificados" className="underline">
              Ir para Certificados
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {podeCadastrar && !avisoNoCabecalho && (
        <AcervoImportCard
          progresso={acervo.progresso}
          onCancelar={cancelarImport}
          onDispensar={acervo.dispensar}
        />
      )}

      {/* Com processos marcados no celular, o rodapé é da seleção (abaixo), não do "+". */}
      {podeCadastrar && !(noCelular && selecionados.size > 0) && (
        <Fab icon={<Plus className="h-6 w-6" />} label="Adicionar Processo" onClick={() => setCreateOpen(true)} />
      )}

      {/* Inline error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {searchQuery && isProcessNumber && !naLista && !isTracked && (
        <ConsultaProcessualCard
          processo={processoExterno ?? undefined}
          isLoading={loadingExterno}
          isPreparing={preparandoExterno}
          prepareTimedOut={consultaExpirada}
          onRetry={externalQuery.retryInitialLoad}
          error={erroExterno}
          isTracked={isTracked}
          searchQuery={searchQuery}
          onAcompanhar={handleAcompanhar}
          onAcompanharLoading={acompanharLoading}
        />
      )}

      <ProcessoFilterBar
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        strictness={search.strictness}
        onStrictnessChange={search.setStrictness}
        resultCount={data ? search.results.length : undefined}
        totalCount={data ? search.total : undefined}
        aba={aba}
        onAbaChange={setAba}
        contagemDaAba={data ? filteredByControls.length : undefined}
        quemCuida={
          // Só o gestor filtra por quem cuida (o advogado comum já vê só o que é dele), e só com
          // colegas: no escritório em que está sozinho, não há de quem filtrar.
          souGestor && temColegas && visaoAtiva && !porVencimento
            ? {
                value: quemCuida,
                onChange: setQuemCuida,
                opcoes: opcoesQuemCuida,
                padrao: quemCuidaPadrao,
              }
            : undefined
        }
        vinculo={vinculo}
        onVinculoChange={setVinculo}
        stats={stats}
        tribunal={tribunal}
        onTribunalChange={setTribunal}
        tribunais={tribunais}
        instancia={instancia}
        onInstanciaChange={setInstancia}
        hoje={hojeLocal()}
        vencimento={porVencimento ? { de: venceDe, ate: venceAte } : null}
        onVencimentoChange={mudarVencimento}
        filtroTags={filtroTags}
        onFiltroTagsChange={setFiltroTags}
        ordenarPorVencimento={ordenarPorVencimento}
        onOrdenarPorVencimentoChange={setOrdenarPorVencimento}
        // No celular a seleção vai para o rodapé: ela começa por um toque longo no meio da
        // lista, longe da barra lá em cima (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.5).
        selecao={podeColaborar && !noCelular ? selecaoEmLote : undefined}
        autoFocusSearch
      />

      <ProcessoTable
        processos={filteredData}
        isLoading={isLoading}
        onEdit={handleEdit}
        onArchive={handleArchiveProcess}
        selecionados={selecionados}
        onSelecionadosChange={setSelecionados}
        onTagsAbertas={filtroTags.length ? setEmEdicao : undefined}
        pagina={pagina}
        onPaginaChange={mudarPagina}
        onDistribuir={
          distribuicao
            ? (p) =>
                setDistribuindo({
                  ids: [p.id],
                  rotulo: `Processo ${p.numero}`,
                  iniciais: undefined,
                })
            : undefined
        }
        distribuivel={distribuicao ? distribuivel : undefined}
        foraDoFiltro={
          buscadoForaDeQuemCuida
            ? {
                filtro: opcoesQuemCuida.find((o) => o.value === quemCuida)?.label ?? 'Você',
                onVerTodos: () => setQuemCuida('todos'),
              }
            : undefined
        }
      />

      {podeColaborar && noCelular && selecionados.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-2 border-t bg-[var(--color-bg-default)] px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-lg">
          <span className="min-w-0 flex-1 truncate text-sm font-medium tabular-nums">
            {selecionados.size} {selecionados.size === 1 ? 'selecionado' : 'selecionados'}
          </span>
          {selecaoEmLote}
        </div>
      )}

      {distribuicao && (
        <ResponsaveisDialog
          escritorioId={distribuicao.escritorioId}
          processoIds={distribuindo?.ids ?? []}
          rotulo={distribuindo?.rotulo ?? ''}
          open={!!distribuindo}
          onOpenChange={(v) => !v && setDistribuindo(null)}
          membros={distribuicao.membros}
          equipes={distribuicao.equipes}
          iniciais={distribuindo?.iniciais}
          onSalvo={() => setSelecionados(new Set())}
        />
      )}

      <TagsLoteDialog
        open={loteAberto}
        onOpenChange={setLoteAberto}
        quantidade={quantidadeDeTags}
        rotulo={quantidadeDeTags === 1 ? 'processo' : 'processos'}
        aplicando={lote.isPending}
        onAplicar={aplicarLote}
      />

      <ProcessoCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      <ProcessoEditDialog
        open={editOpen}
        onOpenChange={(val) => { if (!val) { setEditOpen(false); setSelectedProcess(null); } else setEditOpen(val); }}
        processo={selectedProcess}
      />
    </div>
  );
}

export default ProcessesPage;
